import { Button, Dialog, TextField } from "@mui/material";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSnackbar } from "notistack";
import { ApiRequest } from "GlobalFunctions";
import ImageUploader from "elements/ImageUploader";


import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ImageActions } from '@xeger/quill-image-actions';
import { ImageFormats } from '@xeger/quill-image-formats';
import ResizeModule from "@botom/quill-resize-module";

// Add this interface for the image upload response
interface ImageUploadResponse {
  imageUrl: string;
}


Quill.register('modules/imageActions', ImageActions, true);
Quill.register('modules/imageFormats', ImageFormats, true);
Quill.register("modules/resize", ResizeModule, true);

// Definir los formatos permitidos fuera del componente
const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'blockquote', 'code-block',
  'list', 'bullet',
  'align',
  'link', 'image', 'video', // Add video to formats
  'width', 'height', 'float',
  'color'
];

export default function BlogEditor(props: any) {
  const { open, setOpen, loadList } = props;
  const { enqueueSnackbar } = useSnackbar();


  const defaultValues = {
    title: '',
    content: '',
    image: '',
  };

  const [formValues, setFormValues] = useState(defaultValues);

  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<any>([]);
  const quillRef = useRef<any>(null);


  useEffect(() => {
    if (open) {
      setFormValues(defaultValues);
      setFiles([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isFormValid = () => {
    return (
        formValues.title !== '' &&
        formValues.content !== ''
    );
  };

  const handleSubmit = async () => { 
    setLoading(true);

    const body = new FormData();
    body.append('formValues', JSON.stringify(formValues));
    if (files) {
      body.append("image", files);
    }

    await ApiRequest({
        url: '/blog/create',
        method: 'POST',
        formData: true,
        body: body,
        setResponse: (response: any) => {
            enqueueSnackbar('Entrada creada correctamente', { variant: 'success' });
            setOpen(false);
            loadList();
            //clean form 
            setFormValues(defaultValues);
        }
    });

    setLoading(false);

  };

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (file && quillRef.current) {
        const formData = new FormData();
        formData.append('image', file);

        try {
          await ApiRequest({
            url: '/blog/upload-image',
            method: 'POST',
            formData: true,
            body: formData,
            setResponse: (data: ImageUploadResponse) => {
              const editor = quillRef.current.getEditor();
              const range = editor.getSelection(true);
              editor.insertText(range.index, '\n');
              editor.insertEmbed(range.index, 'image', data.imageUrl);
              editor.setSelection(range.index + 2);
            }
          });
        } catch (error) {
          enqueueSnackbar('Error al subir la imagen', { variant: 'error' });
        }
      }
    };
  }, [enqueueSnackbar]);

  // Helper function to format YouTube URLs
  const formatYouTubeUrl = useCallback((url: string): string => {
    if (!url) return url;
    
    // Extract YouTube video ID from various URL formats
    let videoId = null;
    
    // Standard YouTube watch URL with v parameter
    const watchRegex = /(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.+&v=)([^&]+)/;
    const watchMatch = url.match(watchRegex);
    
    // Short youtu.be URL
    const shortRegex = /youtu\.be\/([^?&]+)/;
    const shortMatch = url.match(shortRegex);
    
    // Embed URL
    const embedRegex = /youtube\.com\/embed\/([^?&]+)/;
    const embedMatch = url.match(embedRegex);
    
    if (watchMatch && watchMatch[1]) {
      videoId = watchMatch[1];
    } else if (shortMatch && shortMatch[1]) {
      videoId = shortMatch[1];
    } else if (embedMatch && embedMatch[1]) {
      videoId = embedMatch[1];
    }
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    return url; // Return original URL if not a YouTube URL or already formatted
  }, []);

  // Add video handler for the toolbar
  const videoHandler = useCallback(() => {
    const url = prompt('Enter the video URL:');
    if (url && quillRef.current) {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection(true);
      
      // Format the URL if it's a YouTube link
      const formattedUrl = formatYouTubeUrl(url);
      
      // Insert video embed
      editor.insertText(range.index, '\n');
      editor.insertEmbed(range.index, 'video', formattedUrl);
      editor.setSelection(range.index + 2);
    }
  }, [formatYouTubeUrl]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }], 
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image', 'video'], // Add video to toolbar
        ['clean']
      ],
      handlers: {
        image: imageHandler,
        video: videoHandler // Add video handler
      }
    },
    imageActions: {
      modules: ['Resize', 'DisplaySize'],
      toolbar: [
        'float',
        'align',
        'resize'
      ]
    },
    imageFormats: {},
    resize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize'], // Removed 'Toolbar' to remove +/- buttons
      overlayStyles: {
        position: 'absolute',
        boxSizing: 'border-box',
        border: '1px dashed #444',
      },
      handleStyles: {
        position: 'absolute',
        height: '12px',
        width: '12px',
        backgroundColor: 'white',
        border: '1px solid #777',
        boxSizing: 'border-box',
        opacity: '0.80',
      },
      displaySize: true,
      handleSides: ['right', 'bottom', 'corner'],
      allowDeselect: true,
      toolbarStyles: {
        position: 'absolute',
        top: '-12px',
        right: '0',
        left: '0',
        height: '0',
        minWidth: '100px',
        font: '12px/1.0 Arial, Helvetica, sans-serif',
        textAlign: 'center',
        color: '#333',
        boxSizing: 'border-box',
        cursor: 'default',
      },
      imageSelectors: ['.ql-editor img'],
      videoSelectors: ['.ql-editor iframe.ql-video'],
      selectionElements: ['img', 'iframe.ql-video'],
    },
    clipboard: {
      matchVisual: false
    }
  }), [imageHandler, videoHandler]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth> 
      <div className="DialogContainer" >
        <div className="ModuleWrapper">
            <h2>Añadir entrada de blog</h2>
          
          <div className="input_group">
            <ImageUploader 
              className="blog_image_uploader"
              image={formValues.image} 
              setImage={(newImage) => setFormValues({ ...formValues, image: newImage })}
              setFiles={setFiles} 
            />
          </div>
          <div className="input_group">
            <TextField
              label="Título"
              variant="outlined"
              fullWidth
              value={formValues.title}
              onChange={(e) => setFormValues({ ...formValues, title: e.target.value })}
            />
          </div>
          <div className="input_group">
            <ReactQuill
              ref={quillRef}
              value={formValues.content}
              onChange={(content) => setFormValues({ ...formValues, content })}
              style={{ height: '300px', marginBottom: '50px' }}
              modules={modules}
              formats={formats}
              theme="snow"
            />
          </div>
        </div>
      </div>
      <div className="dialogButtons">
            <Button onClick={() => setOpen(false)} variant="outlined">Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!isFormValid() || loading}>Crear entrada</Button>
          </div>
      
    </Dialog>
  );
}